import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

export default function LeadAssignmentPanel({
  companyName,
  assignment,
  canManageAssignments = false,
  assignableUsers = [],
  assignableUsersLoading = false,
  postJson,
  deleteJson,
  onAssignmentsChanged,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [assignmentComment, setAssignmentComment] = useState("");
  const [assignmentError, setAssignmentError] = useState("");
  const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);

  const activeAssignment = assignment || null;
  const hasAssignment = Boolean(activeAssignment?.assignedTo);
  const assigneeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...assignableUsers.map((entry) => normalizeEmail(entry)),
            normalizeEmail(activeAssignment?.assignedTo),
          ].filter(Boolean)
        )
      ).sort(),
    [activeAssignment?.assignedTo, assignableUsers]
  );

  useEffect(() => {
    if (!dialogOpen || !canManageAssignments) {
      return;
    }

    setSelectedAssignee(activeAssignment?.assignedTo || "");
  }, [activeAssignment?.assignedTo, canManageAssignments, dialogOpen]);

  const resetForm = () => {
    setSelectedAssignee("");
    setAssignmentComment("");
    setAssignmentError("");
  };

  const openDialog = (event) => {
    event?.stopPropagation?.();
    setAssignmentError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (isAssignmentSaving) {
      return;
    }

    setDialogOpen(false);
    setAssignmentError("");
  };

  const assignCompany = async () => {
    if (!postJson || !canManageAssignments) {
      return;
    }

    const normalizedAssignee = normalizeEmail(selectedAssignee);
    if (!normalizedAssignee) {
      setAssignmentError("Assignee is required.");
      return;
    }

    const trimmedComment = assignmentComment.trim();
    if (!trimmedComment) {
      setAssignmentError("Comment is required.");
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError("");

    try {
      await postJson(`/api/lead-management/assign/${encodeURIComponent(companyName)}`, {
        assignTo: normalizedAssignee,
        comment: trimmedComment,
      });
      await onAssignmentsChanged?.();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Unable to assign company:", error);
      setAssignmentError(error.message || "Unable to assign company.");
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  const removeAssignment = async () => {
    if (!deleteJson || !canManageAssignments || !hasAssignment) {
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError("");

    try {
      await deleteJson(`/api/lead-management/assign/${encodeURIComponent(companyName)}/remove`, {
        comment: assignmentComment.trim(),
      });
      await onAssignmentsChanged?.();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Unable to remove assignment:", error);
      setAssignmentError(error.message || "Unable to remove assignment.");
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  if (!canManageAssignments && !hasAssignment) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: "grid",
          gap: 1,
          justifyItems: "start",
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>Lead Assignment:</Typography>
        <Button
          variant="contained"
          onClick={openDialog}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {canManageAssignments && !hasAssignment ? "Assign" : "View Assignment"}
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Lead Assignment</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, mt: 0.5, color: "#475569" }}>
            {hasAssignment
              ? `Assignment details for ${companyName}.`
              : `Assign ${companyName} to a user.`}
          </Typography>

          <Box sx={{ display: "grid", gap: 1.5 }}>
            {hasAssignment ? (
              <Box
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 2,
                  backgroundColor: "#f8fafc",
                  p: 1.25,
                  display: "grid",
                  gap: 0.75,
                }}
              >
                <Typography sx={{ fontSize: "0.92rem", color: "#1e293b" }}>
                  <strong>Assigned To:</strong> {activeAssignment.assignedTo}
                </Typography>
                <Typography sx={{ fontSize: "0.92rem", color: "#475569" }}>
                  <strong>Assigned By:</strong> {activeAssignment.assignedBy || "Unknown"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.92rem",
                    color: "#475569",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  <strong>Assignment Comment:</strong> {activeAssignment.comment || "None"}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
                No current assignment.
              </Typography>
            )}

            {canManageAssignments && (
              <>
                <Autocomplete
                  freeSolo
                  options={assigneeOptions}
                  loading={assignableUsersLoading}
                  value={selectedAssignee}
                  onChange={(event, value) => {
                    setSelectedAssignee(typeof value === "string" ? value : value || "");
                    if (assignmentError) {
                      setAssignmentError("");
                    }
                  }}
                  onInputChange={(event, value) => {
                    setSelectedAssignee(value);
                    if (assignmentError) {
                      setAssignmentError("");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign To"
                      size="small"
                      helperText={
                        assignableUsersLoading
                          ? "Loading assignee options..."
                          : "Select a known user or type an email."
                      }
                    />
                  )}
                />

                <TextField
                  label={hasAssignment ? "Assignment Update Comment" : "Assignment Comment"}
                  multiline
                  minRows={3}
                  fullWidth
                  value={assignmentComment}
                  helperText={
                    hasAssignment ? "Optional for removal, recommended for reassignment." : undefined
                  }
                  onChange={(event) => {
                    setAssignmentComment(event.target.value);
                    if (assignmentError) {
                      setAssignmentError("");
                    }
                  }}
                />

                {assignmentError && (
                  <Typography sx={{ fontSize: "0.85rem", color: "#b91c1c" }}>
                    {assignmentError}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap", justifyContent: "space-between" }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {canManageAssignments && (
              <Button
                variant="contained"
                onClick={assignCompany}
                disabled={isAssignmentSaving}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {isAssignmentSaving ? "Saving..." : hasAssignment ? "Reassign" : "Assign"}
              </Button>
            )}
            {canManageAssignments && hasAssignment && (
              <Button
                variant="outlined"
                color="error"
                onClick={removeAssignment}
                disabled={isAssignmentSaving}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Remove Assignment
              </Button>
            )}
          </Box>
          <Button onClick={closeDialog} disabled={isAssignmentSaving}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
